import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  listCorporateRentalDiscount,
  addCorporateRentalDiscount,
  updateCorporateRentalDiscount,
  statusCorporateRentalDiscount,
} from "../../Redux/Actions/corporateRentalDiscountAction";
import { columnsCorporateRentalDiscountList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import CorporateRentalDiscountEditModal from "./addAndUpdateCorporateRentalDiscount";
import { Button, Modal } from "antd";

const ListCorporateRentalDiscount = () => {
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
  const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

  const fetchDetails = useCallback(
    (showLoader = true) => {
      const payload = {
        page: pagination.current,
        limit: pagination.pageSize,
        filter: debouncedSearch,
        ...(dateRange.start && {
          start_date: dateRange.start.format("YYYY-MM-DD"),
        }),
        ...(dateRange.end && {
          end_date: dateRange.end.format("YYYY-MM-DD"),
        }),
      };

      return dispatch(listCorporateRentalDiscount(payload, showLoader)).catch(() => {
        if (showLoader) {
          toast.error("Failed to fetch corporate rental discount list");
        }
        return Promise.reject();
      });
    },
    [
      dispatch,
      pagination.current,
      pagination.pageSize,
      debouncedSearch,
      dateRange,
    ]
  );

  const corporateRentalDiscountState = useSelector((state) => state.corporateRentalDiscount || {});
  const discounts = useMemo(() => {
    return corporateRentalDiscountState.corporateRentalDiscountList?.data || [];
  }, [corporateRentalDiscountState.corporateRentalDiscountList]);
  const discountTotal = useMemo(() => {
    return corporateRentalDiscountState.corporateRentalDiscountList?.count || 0;
  }, [corporateRentalDiscountState.corporateRentalDiscountList]);
  const { loading } = corporateRentalDiscountState;

  useEffect(() => {
    fetchDetails(true);
  }, [fetchDetails]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: discountTotal }));
  }, [discountTotal]);

  const handleDateRangeApply = (start, end) => {
    setDateRange({ start, end });
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const openEditModal = useCallback((discount = null) => {
    setSelectedDiscount(discount);
    setIsModalOpen(true);
  }, []);

  const closeEditModal = () => {
    setSelectedDiscount(null);
    setIsModalOpen(false);
  };

  const handleStatusToggle = useCallback(
    (record) => {
      const nextStatus = record.status === "active" ? "inactive" : "active";
      Modal.confirm({
        title: "Confirm Status Change",
        content: `Are you sure you want to mark this discount as "${nextStatus}"?`,
        okText: "Yes, Confirm",
        onOk: async () => {
          try {
            await dispatch(statusCorporateRentalDiscount({ id: record._id, status: nextStatus }));
            fetchDetails(false);
          } catch (error) {
            toast.error("Failed to update status");
          }
        },
      });
    },
    [dispatch, fetchDetails]
  );

  const handleSubmit = async (values) => {
    try {
      let result;

      if (selectedDiscount?._id) {
        result = await dispatch(
          updateCorporateRentalDiscount({
            id: selectedDiscount._id,
            ...values,
          })
        );
      } else {
        result = await dispatch(
          addCorporateRentalDiscount({
            ...values,
          })
        );
      }

      if (result) {
        fetchDetails(false);
        closeEditModal();
        return { success: true };
      }
    } catch (error) {
      toast.error("Failed to save discount");
      return { success: false };
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
    fetchDetails(false);
  };

  return (
    <>
      <div
        style={{ position: "absolute", marginTop: "9.6%", marginLeft: "90.8%" }}
      >
        {canAdd && (
          <Button
            style={{
              background: "#070C18",
              color: "white",
              padding: "18px",
              borderRadius: "20px",
              width: "100px",
            }}
            onClick={() => openEditModal(null)}
          >
            Add
          </Button>
        )}
      </div>

      <TableComponent
        pagination={pagination}
        setState={setSearchQuery}
        dataSource={discounts}
        onChange={handleTableChange}
        h3heading="Corporate Rental Discount List"
        onCalendarClick={() => setIsDatePopupVisible(true)}
        columns={columnsCorporateRentalDiscountList(
          pagination.current,
          pagination.pageSize,
          handleStatusToggle,
          openEditModal,
          canEdit
        )}
        loading={loading}
      />

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />

      {isModalOpen && (
        <CorporateRentalDiscountEditModal
          visible={isModalOpen}
          discountData={selectedDiscount}
          onSave={handleSubmit}
          onClose={closeEditModal}
        />
      )}
    </>
  );
};

export default ListCorporateRentalDiscount;